<template>
  <div class="pagination">
    <router-link
      class="pagination_arrow pagination_prev"
      :class="[{ disabled: isFirstPage }]"
      :to="go(-1)"
    />
    <template v-for="page in pagenationNums" :key="page">
      <span v-if="page === currentPage" class="pagination_num">
        {{ page }}
      </span>
      <router-link v-else :to="push(page)" class="pagination_num">
        {{ page }}
      </router-link>
    </template>
    <router-link
      class="pagination_arrow pagination_next"
      :class="[{ disabled: isLastPage }]"
      :to="go(1)"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, Ref, watch } from 'vue'
import { useRoute } from 'vue-router'

export default defineComponent({
  name: 'Pagination',
  props: {
    totalPages: {
      type: Number,
      default: 1,
      required: true
    }
  },
  setup(props) {
    const route = useRoute()
    const currentPage = computed(() => Number(route.query.page) || 1)
    const allPages = computed(() =>
      [...Array(props.totalPages).keys()].map((n) => n + 1)
    )
    const go = (num: number) => ({
      query: { ...route.query, page: currentPage.value + num }
    })
    const push = (num: number) => ({
      query: { ...route.query, page: num }
    })
    const pagenationNums: Ref<number[]> = ref([])
    const maxDisplayPageNum = 5 //最大ページネーション数
    const halfDisplayPageNum = Math.floor(maxDisplayPageNum / 2)

    watch(
      () => [currentPage.value, allPages.value],
      () => {
        // ページ数が、最大ページング表示数以下であれば全ページそのまま表示
        if (allPages.value.length <= maxDisplayPageNum) {
          pagenationNums.value = allPages.value
          return
        }
        // 現在ページが、最初のページ数から最大ページング表示数の半分の間は
        // ページングは1から最大ページング数を表示
        if (currentPage.value <= allPages.value[halfDisplayPageNum - 1]) {
          pagenationNums.value = allPages.value.slice(0, maxDisplayPageNum)
          return
        }
        // 現在ページが、ページ数最後から最大ページング表示数の半分の間
        // ページングは最終ページから最大ページング数を表示
        if (
          currentPage.value >=
          allPages.value[allPages.value.length - halfDisplayPageNum]
        ) {
          pagenationNums.value = allPages.value.slice(-1 * maxDisplayPageNum)
          return
        }
        // それ以外は
        // 現在ページと、±最大ページング数の半分を表示
        pagenationNums.value = [...Array(maxDisplayPageNum).keys()].map(
          (n) => currentPage.value - halfDisplayPageNum + n
        )
        return
      },
      {
        immediate: true
      }
    )

    const isFirstPage = computed(
      () => currentPage.value === pagenationNums.value[0]
    )
    const isLastPage = computed(
      () =>
        currentPage.value ===
        pagenationNums.value[pagenationNums.value.length - 1]
    )

    return {
      allPages,
      pagenationNums,
      halfDisplayPageNum,
      currentPage,
      isFirstPage,
      isLastPage,
      push,
      go
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1.8rem;
}

.pagination_arrow {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 13vw;
  height: 13vw;
  max-width: 50px;
  max-height: 50px;
  background-color: var.$color-white;
  border: 1px solid var.$color-border-gray;
  border-radius: 10px;
  box-shadow: 0 6px 9px rgba(0, 0, 0, 0.08);
  font-size: 1.4rem;
  color: inherit;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
  &::after {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    border-right: 2px solid var.$color-main;
    border-bottom: 2px solid var.$color-main;
    transform: rotate(-45deg) translateY(-1px);
    transition: border-color 0.1s;
  }
}

.pagination_prev {
  &::after {
    transform: rotate(135deg) translateY(-1px);
  }
}

a.pagination_num {
  color: var.$color-main-text;
}
a.disabled {
  visibility: hidden;
}
</style>
